import { FC, useEffect } from 'react';
import { z } from 'zod';
import { graphql } from '../../../gql';
import { Grid, OutlinedInput } from '@mui/material';
import { useIntl } from 'react-intl';
import { ModalContent } from '../../../modal/components/ModalContent';
import { FormField } from '../../../form/FormField';
import { ModalActions } from '../../../modal/components/ModalActions';
import { useTypedForm } from '../../../../../shared/fe/hooks/useForm';
import { useModalContext } from '../../../modal/ModalContext';
import { useApiMutation, useApiQuery } from '../../../query/hooks';
import { mutationResponseHandler } from '../../../utils/mutationResponseHandler';
import { useSnackbar } from '../../../store/snackbar';

const updateWidgetNameMutation = graphql(`
	mutation detail_updateWidget($widgetId: UUID!, $name: String) {
		updateWidget(by: { id: $widgetId }, data: { name: $name }) {
			ok
			errorMessage
		}
	}
`);

const getWidgetQuery = graphql(`
	query detail_getWidget($widgetId: UUID!) {
		getWidget(by: { id: $widgetId }) {
			type
			name
		}
	}
`);

const editWidgetNameModalSchema = z.object({
	id: z.string(),
});

type Props = z.infer<typeof editWidgetNameModalSchema>;

const formSchema = z.object({
	name: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

const EditWidgetNameModal: FC<Props> = ({ id }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { openSnackbar } = useSnackbar();
	const { mutate: updateWidgetName } = useApiMutation(updateWidgetNameMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.updateWidget.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'widget.name.success.update' }),
					error: intl.formatMessage({ id: 'widget.name.error.update' }),
				},
				errorMessage: result.updateWidget.errorMessage,
				callback: () => closeModal(),
				openSnackbar,
			}),
	});
	const { data } = useApiQuery(getWidgetQuery, { variables: { widgetId: id }, enabled: id !== '' });
	const widget = data?.getWidget;

	const form = useTypedForm({
		schema: formSchema,
	});

	useEffect(() => {
		form.setValue('name', widget?.name ?? '');
	}, [form, widget?.name]);

	const submitDisabled = !form.formState.isValid;

	const onSubmit = ({ name }: FormValues) => {
		updateWidgetName({ widgetId: id, name });
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'form.widget.title' })}>
				<form
					noValidate
					onSubmit={(e) => {
						e.preventDefault();
						if (form.formState.isValid) {
							updateWidgetName({ widgetId: id, name: form.getValues('name') });
							closeModal();
						}
					}}
				>
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'form.widget.label.name' })}
							render={(props) => <OutlinedInput placeholder={widget?.type} {...props} />}
						/>
					</Grid>
				</form>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'form.widget.title.submit' })}
				disablePrimaryButton={submitDisabled}
			/>
		</>
	);
};

export { EditWidgetNameModal, editWidgetNameModalSchema };
